import { useState, useEffect, useCallback, useRef } from 'react'
import { useQueryClient, onlineManager } from '@tanstack/react-query'

import { apiClient } from '@/shared/lib/api-client'
import { serverReachability } from '@/shared/lib/server-reachability'
import { openGate } from '@/shared/lib/query-client'
import {
  getDirtyScores,
  getDirtyActivations,
  type ScoreWithDirty,
  type JudgeStateWithDirty,
} from '@/features/scoring/api'
import type { Score } from '@app/shared/types/entities'

export type SyncStatus = 'idle' | 'syncing' | 'error'

type SyncScoreItem = {
  participationId: string
  baseUpdatedAt: string | null
  questionsData: Array<{ question: number; mistakes: string[] }>
  stage2Data: Record<string, number>
  submitted: boolean
}

type SyncResult = {
  participationId: string
  status: 'ok' | 'conflict' | 'error'
  score?: Score
  error?: string
}

type SyncResponse = {
  results: SyncResult[]
}

export type ConflictItem = {
  participationId: string
  localScore: ScoreWithDirty
  serverScore: Score
}

const MAX_SYNC_RETRIES = 3

export function useOfflineSync(competitionId: string): {
  syncStatus: SyncStatus
  hasDirtyData: boolean
  conflicts: ConflictItem[]
  resolveConflict: (participationId: string, choice: 'local' | 'server') => void
  retrySyncConflicts: () => Promise<void>
} {
  const queryClient = useQueryClient()
  const [syncStatus, setSyncStatus] = useState<SyncStatus>('idle')
  const [hasDirtyData, setHasDirtyData] = useState(false)
  const [conflicts, setConflicts] = useState<ConflictItem[]>([])
  const retryCountRef = useRef(0)
  const syncingRef = useRef(false)

  // Keep hasDirtyData in sync with cache
  useEffect(() => {
    function check(): void {
      const hasDirtyScores = getDirtyScores(queryClient).length > 0
      const hasActivations = getDirtyActivations(queryClient, competitionId).length > 0
      setHasDirtyData(hasDirtyScores || hasActivations)
    }
    check()
    const unsubscribe = queryClient.getQueryCache().subscribe((event) => {
      if (
        event.query.queryKey[0] === 'my-score' ||
        (event.query.queryKey[0] === 'competitions' && event.query.queryKey[2] === 'judge-state')
      ) {
        check()
      }
    })
    return () => unsubscribe()
  }, [queryClient, competitionId])

  const sync = useCallback(async () => {
    if (syncingRef.current) {
      console.log('[SYNC] already syncing, skipping')
      return
    }
    if (serverReachability.getState() !== 'reachable') {
      console.log('[SYNC] server not reachable, skipping')
      return
    }

    const dirtyScores = getDirtyScores(queryClient)
    const activations = getDirtyActivations(queryClient, competitionId)
    console.log(
      '[SYNC] dirty scores:',
      dirtyScores.length,
      'activations:',
      activations.length,
      'gate:',
      onlineManager.isOnline() ? 'open' : 'closed',
    )

    if (dirtyScores.length === 0 && activations.length === 0) {
      console.log('[SYNC] no dirty data, opening gate')
      openGate()
      return
    }

    syncingRef.current = true
    setSyncStatus('syncing')

    let shouldOpenGate = false

    try {
      // 1. Sync offline activations (super judge only)
      if (activations.length > 0) {
        console.log('[SYNC] syncing', activations.length, 'offline activations:', activations)
        for (const pid of activations) {
          try {
            await apiClient.post(`/api/participations/${pid}/activate`)
            console.log('[SYNC] activated:', pid)
          } catch (err) {
            console.error('[SYNC] activation failed:', pid, err)
            // Continue — score sync doesn't depend on activation success
          }
        }
        // Clear dirty activations from judge-state cache
        queryClient.setQueryData<JudgeStateWithDirty>(
          ['competitions', competitionId, 'judge-state'],
          (old) => (old ? { ...old, _dirtyActivations: [] } : old),
        )
      }

      // 2. Sync dirty scores
      if (dirtyScores.length > 0) {
        console.log(
          '[SYNC] starting score sync for',
          dirtyScores.length,
          'scores:',
          dirtyScores.map((d) => d.participationId),
        )

        const payload: { scores: SyncScoreItem[] } = {
          scores: dirtyScores.map(({ participationId, score }) => ({
            participationId,
            baseUpdatedAt: score.updatedAt ?? null,
            questionsData: score.questionsData,
            stage2Data: score.stage2Data,
            submitted: score._pendingSubmit === true,
          })),
        }

        const response = await apiClient.post<SyncResponse>('/api/scores/sync', payload)

        console.log(
          '[SYNC] response:',
          response.results.map((r) => `${r.participationId}: ${r.status}`),
        )

        const newConflicts: ConflictItem[] = []

        for (const result of response.results) {
          if (result.status === 'ok' && result.score) {
            queryClient.setQueryData<Score>(['my-score', result.participationId], result.score)
            console.log('[SYNC] ok:', result.participationId)
          } else if (result.status === 'conflict' && result.score) {
            const localEntry = dirtyScores.find(
              (d) => d.participationId === result.participationId,
            )
            if (localEntry) {
              newConflicts.push({
                participationId: result.participationId,
                localScore: localEntry.score,
                serverScore: result.score,
              })
            }
            console.log('[SYNC] conflict:', result.participationId)
          } else if (result.status === 'error') {
            console.log('[SYNC] error:', result.participationId, result.error)
          }
        }

        if (newConflicts.length > 0) {
          setConflicts(newConflicts)
        }
      }

      retryCountRef.current = 0
      setSyncStatus('idle')
      shouldOpenGate = true
      console.log('[SYNC] complete, opening gate')
    } catch (err) {
      retryCountRef.current++
      console.error(
        '[SYNC] failed (attempt',
        retryCountRef.current,
        '/',
        MAX_SYNC_RETRIES,
        '):',
        err,
      )

      if (retryCountRef.current >= MAX_SYNC_RETRIES) {
        setSyncStatus('error')
        shouldOpenGate = true
        console.log('[SYNC] max retries reached, opening gate anyway')
      } else {
        setSyncStatus('error')
      }
    } finally {
      syncingRef.current = false
      if (shouldOpenGate) {
        openGate()
      }
    }
  }, [queryClient, competitionId])

  const resolveConflict = useCallback(
    (participationId: string, choice: 'local' | 'server') => {
      const conflict = conflicts.find((c) => c.participationId === participationId)
      if (!conflict) return

      if (choice === 'server') {
        queryClient.setQueryData<Score>(['my-score', participationId], conflict.serverScore)
      }

      setConflicts((prev) => prev.filter((c) => c.participationId !== participationId))
      console.log('[SYNC] conflict resolved:', participationId, '→', choice)
    },
    [conflicts, queryClient],
  )

  const retrySyncConflicts = useCallback(async () => {
    await sync()
  }, [sync])

  // Trigger sync on mount and on reachability change
  useEffect(() => {
    console.log('[SYNC] hook mounted, running initial sync')
    sync()

    return serverReachability.subscribe((state) => {
      console.log('[SYNC] reachability changed to', state)
      if (state === 'reachable') {
        sync()
      }
    })
  }, [sync])

  return { syncStatus, hasDirtyData, conflicts, resolveConflict, retrySyncConflicts }
}
